// "Bluetooth is off" — the banner, on every screen that scans.
//
// ── Why this exists as a component and not a line of text ─────────────────
//
// A scan with the adapter switched off is the quietest failure in the app: the
// plugin's `requestLEScan` resolves, its callback is never invoked, and the scan
// runs its full 15-second timeout to an empty list. On screen that is
// indistinguishable from "no devices are advertising" — so it gets debugged as a
// firmware fault, which is exactly what happened. See ensureBleReady in
// core/ble/bleClient for the plugin source that makes it silent.
//
// Two screens scan (BLE Test, and Wi-Fi onboarding) and a third controls over
// BLE while offline. All of them need the same words and the same button, and a
// warning that says one thing here and another there is its own small bug.
//
// ⚠️ THE BUTTON IS ANDROID-ONLY, AND THE FALLBACK IS NOT A LESSER VERSION OF IT.
// `requestEnable` shows Android's own "an app wants to turn on Bluetooth"
// dialog. On iOS an app may not turn the user's radio on at all — the plugin
// answers `unavailable` — so there the only honest thing is to say so in words.
// `requestBleEnable` returns false rather than throwing for exactly this reason:
// "we were not allowed to ask" is a normal outcome.

import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';
import { isNativePlatform } from '../../../core/platform';

export default function BluetoothOffBanner({ onTurnOn, onOpenSettings, compact = false }) {
  const theme = useTheme();
  const c = theme.sli;
  // Capacitor on iOS cannot raise the enable dialog; anywhere else we can try.
  const canAsk = isNativePlatform();

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mx: 1.5,
      my: 1,
      px: 1.5,
      py: compact ? 1 : 1.25,
      borderRadius: '12px',
      bgcolor: '#E5393522',
      border: '1px solid #E5393566',
    }}
    >
      <BluetoothDisabledIcon sx={{ color: '#EF9A9A' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#EF9A9A', fontWeight: 700, fontSize: 13 }}>
          Bluetooth is off
        </Typography>
        {!compact && (
          <Typography sx={{ color: `${c.text}AA`, fontSize: 11 }}>
            {/* Says what the failure LOOKS like, not just that it exists — the
                whole trap is that the empty list looks like an answer. */}
            Scanning with it off finds nothing and reports nothing.
          </Typography>
        )}
      </Box>
      {canAsk ? (
        <Button
          size="small"
          variant="contained"
          onClick={onTurnOn}
          sx={{ bgcolor: '#E53935', color: '#fff', textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Turn on
        </Button>
      ) : (
        <Button
          size="small"
          onClick={onOpenSettings}
          sx={{ color: '#EF9A9A', textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Settings
        </Button>
      )}
    </Box>
  );
}
